import { useState } from 'react'
import {
  clone,
  formatValues,
  formatNumberOnUnitMeasure,
  generateAllCombinations,
  getBadgeForDataTable,
  getBadgesElement,
  getDataTableHeaders,
  getNameFromDisaggregation,
  getRecentYearRange,
  formatUnitMult,
  formatUnitMeasure,
  getUnitMeasureItems,
  isNumeric,
} from '../common/utils/helper'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import {
  chartTypes,
  disaggregations,
  periodicity,
} from '../common/utils/enum'
import {
  getBenchmarkList,
  getSingleYearChartSubtitle,
} from './charts/common/helper'
import NoDataImage from '../common/components/NoDataImage'
import { NONE } from '../common/utils/constants'

const DataTable = ({
  filteredIndicatorData,
  selectedCountries,
  countriesData,
  currentYear,
  selectedFilters,
  recentYearSelection,
  indicatorName,
  disaggregationData,
  filteredAggregateData,
  noDataImageURL,
  selectedFrequency,
  selectedUnitMeasure,
  setDataTableData,
  unitMeasureName,
  countryList,
  selectedAverages,
  countryHierarchy,
  minYear,
  maxYear,
  selectedAverage,
  isSingleSelectionGrouping,
  disaggregationCountries,
  unitMeasureList,
}) => {
  const [sortOrder, setSortOrder] = useState({ economy: 'asc' })
  let years = []
  if (filteredIndicatorData && filteredIndicatorData.length) {
    years.push(...filteredIndicatorData[0]?.data?.xAxis)
  }
  const finalSelectedAverages = isSingleSelectionGrouping
    ? selectedAverage && selectedAverage !== NONE
      ? [selectedAverage]
      : []
    : selectedAverages

  const dispatch = useDispatch()
  const handleSort = (column) => {
    let tempSortOrder = clone(sortOrder)

    for (const [key, value] of Object.entries(sortOrder)) {
      if (key === column) {
        tempSortOrder[key] = value === 'asc' ? 'desc' : 'asc'
      } else {
        tempSortOrder[key] = ''
      }
    }
    if (!sortOrder.hasOwnProperty(column)) {
      tempSortOrder[column] = 'asc'
    }
    setSortOrder(tempSortOrder)
  }

  const columnCombinations = generateAllCombinations(selectedFilters)
  let headers = getDataTableHeaders(
    columnCombinations,
    disaggregationData?.dimensions
  )
  // for indicators with no unit measure in dimensions object (for eg: IFC_GEM)
  // get unit measure codes from the data response and unit measure names from attributes object
  if (unitMeasureList && unitMeasureList.length) {
    headers = getDataTableHeaders(
      columnCombinations,
      disaggregationData?.dimensions,
      filteredIndicatorData,
      filteredAggregateData
    )
  }
  useEffect(() => {
    const selectedCountryList = selectedCountries.map((selectedCountry) =>
      countriesData.find(
        (country) =>
          country.code.toUpperCase() === selectedCountry.toUpperCase()
      )
    )

    const otherCountryList = countriesData.filter(
      (country) =>
        !selectedCountries.includes(country.code) &&
        countryList.map((item) => item.id).includes(country.code)
    )

    let selectedCountryDataFilter =  selectedCountryList

    let selectedCountryData = selectedCountryDataFilter.map((country) => {
      const combinationData = columnCombinations.map((combination, index) => {
        const data = filteredIndicatorData?.find(
          (item) =>
            item[disaggregations.RefArea] === country.code &&
            Object.entries(combination).every(
              ([key, value]) => item[key] === value
            )
        )

        const year = data?.data?.xAxis?.[0]
        const value = data?.data?.yAxis?.[0]

        return {
          value:
            value && value !== 'null' && value !== 'NaN'
              ? formatValues(value)
              : '--',
          year: year ? year : '--',
        }
      })
      return {
        countryName: country?.description,
        year: currentYear ? currentYear : '--',
        countryId: country?.code,
        combinationData: combinationData,
      }
    })
    let otherCountryData = otherCountryList.map((country) => {
      const combinationData = columnCombinations.map((combination, index) => {
        const data = filteredIndicatorData.find(
          (item) =>
            item[disaggregations.RefArea] === country.code &&
            Object.entries(combination).every(
              ([key, value]) => item[key] === value
            )
        )

        const year = data?.data?.xAxis?.[0]
        const value = data?.data?.yAxis?.[0]

        return {
          value:
            value && value !== 'null' && value !== 'NaN'
              ? formatValues(value)
              : '--',
          year: year ? year : '--',
        }
      })
      return {
        countryName: country?.description,
        year: currentYear ? currentYear : '--',
        countryId: country?.code,
        combinationData: combinationData,
      }
    })
    let sortByColumn = '',
      sortColumnOrder = ''
    for (const [key, value] of Object.entries(sortOrder)) {
      if (value) {
        sortByColumn = key
        sortColumnOrder = value
        break
      }
    }
    let sortedSelectedCountryData = []
    let sortedOtherCountryData = []
    if (sortByColumn === 'economy') {
      sortedSelectedCountryData =
        sortColumnOrder === 'asc'
          ? selectedCountryData.sort((a, b) =>
              !a.countryName
                ? 1
                : !b.countryName
                  ? -1
                  : a.countryName
                      .toLowerCase()
                      .localeCompare(b.countryName.toLowerCase())
            )
          : selectedCountryData.sort((a, b) =>
              !a.countryName
                ? 1
                : !b.countryName
                  ? -1
                  : b.countryName
                      .toLowerCase()
                      .localeCompare(a.countryName.toLowerCase())
            )
      sortedOtherCountryData =
        sortColumnOrder === 'asc'
          ? otherCountryData.sort((a, b) =>
              !a.countryName
                ? 1
                : !b.countryName
                  ? -1
                  : a.countryName
                      .toLowerCase()
                      .localeCompare(b.countryName.toLowerCase())
            )
          : otherCountryData.sort((a, b) =>
              !a.countryName
                ? 1
                : !b.countryName
                  ? -1
                  : b.countryName
                      .toLowerCase()
                      .localeCompare(a.countryName.toLowerCase())
            )
    }

    let finalData = [...sortedSelectedCountryData, ...sortedOtherCountryData]
    if (finalSelectedAverages && finalSelectedAverages.length > 0) {
      let benchmarkList = getBenchmarkList({
        selectedAverages: finalSelectedAverages,
        selectedCountries,
        countryHierarchy,
        chartType: chartTypes.DataTable,
        disaggregationCountries,
      })
      benchmarkList = benchmarkList.filter((item) =>
        countriesData?.map((item) => item?.code).includes(item)
      )
      benchmarkList.forEach((item) => {
        const combinationData = columnCombinations.map((combination, index) => {
          const data = filteredAggregateData.find((item) =>
            // item[disaggregations.RefArea] === country.id &&
            Object.entries(combination).every(
              ([key, value]) => item[key] === value
            )
          )

          const year = data?.data?.xAxis?.[0]
          const value = data?.data?.yAxis?.[0]
          return {
            value:
              value && value !== 'null' && value !== 'NaN'
                ? formatValues(value)
                : '--',
            year: year ? year : '--',
          }
        })
        finalData.unshift({
          countryName: getNameFromDisaggregation(
            disaggregationData?.dimensions,
            disaggregations.RefArea,
            item
          ),
          year: currentYear ? currentYear : '--',
          countryId: item,
          combinationData: combinationData,
        })
      })
    }
    dispatch(setDataTableData(finalData))
  }, [
    countriesData,
    selectedCountries,
    filteredIndicatorData,
    currentYear,
    selectedAverage,
    selectedAverages,
    filteredAggregateData,
    sortOrder,
    recentYearSelection,
  ])

  const allSelectedAverage =
    filteredAggregateData?.map(
      (aggregate) => aggregate?.[disaggregations.RefArea]
    ) ?? []
  const allCountries =  countriesData.filter(
        (country) =>
          !selectedCountries.includes(country.code) &&
          countryList.map((item) => item.id).includes(country.code) &&
          !allSelectedAverage.includes(country.code)
      )

  const renderEconomyHeaders = () => (
    <th
      className="table-heading"
      aria-sort={
        sortOrder['economy'] === 'asc'
          ? 'ascending'
          : sortOrder['economy'] === 'desc'
            ? 'descending'
            : undefined
      }
      style={{ cursor: 'pointer' }}
      onClick={() => handleSort('economy')}
    >
      <span
        className={
          sortOrder['economy'] === 'asc'
            ? 'sorting-arrow-up'
            : sortOrder['economy'] === 'desc'
              ? 'sorting-arrow-down'
              : ''
        }
      >
        Economy
      </span>
    </th>
  )

  const renderDisaggHeaders = () => (
    <>
      {!recentYearSelection
        ? headers.map((header, headerIndex) => {
            const unitMeasure = header.unitMeasureCode
              ? formatUnitMeasure(
                  header.unitMeasureCode,
                  unitMeasureList.find(
                    (item) => item.code === header.unitMeasureCode
                  )?.description,
                  unitMeasureList.find(
                    (item) => item.code === header.unitMeasureCode
                  )?.short_label
                )
              : ''

            return years.map((year) => (
              <th
                key={`${headerIndex}-${year}`}
                className={`table-heading ${
                  sortOrder[`combination${headerIndex}_${year}`] === 'asc'
                    ? 'sorting-arrow-up'
                    : sortOrder[`combination${headerIndex}_${year}`] === 'desc'
                      ? 'sorting-arrow-down'
                      : ''
                }`}
                style={{ cursor: 'pointer' }}
                aria-sort={
                  sortOrder[`combination${headerIndex}_${year}`] === 'asc'
                    ? 'ascending'
                    : sortOrder[`combination${headerIndex}_${year}`] === 'desc'
                      ? 'descending'
                      : undefined
                }
                onClick={() => handleSort(`combination${headerIndex}_${year}`)}
              >
                {header.combinationText}
                <span className="table-heading-year"> ({year})</span>
                <div
                  className="table-heading-year"
                  style={{ fontSize: '0.5rem', lineHeight: '1rem' }}
                >
                  {unitMeasure ? `Unit Measure: ${unitMeasure}` : ''}
                </div>
              </th>
            ))
          })
        : headers.map((header, index) => {
            const unitMeasure = header.unitMeasureCode
              ? formatUnitMeasure(
                  header.unitMeasureCode,
                  unitMeasureList.find(
                    (item) => item.code === header.unitMeasureCode
                  )?.description,
                  unitMeasureList.find(
                    (item) => item.code === header.unitMeasureCode
                  )?.short_label
                )
              : ''
            return (
              <th
                key={index}
                className={`table-heading ${
                  sortOrder[`combination${index}`] === 'asc'
                    ? 'sorting-arrow-up'
                    : sortOrder[`combination${index}`] === 'desc'
                      ? 'sorting-arrow-down'
                      : ''
                }`}
                style={{ cursor: 'pointer' }}
                aria-sort={
                  sortOrder[`combination${index}`] === 'asc'
                    ? 'ascending'
                    : sortOrder[`combination${index}`] === 'desc'
                      ? 'descending'
                      : undefined
                }
                onClick={() => handleSort(`combination${index}`)}
              >
                {header.combinationText}
                <span className="table-heading-year">
                  {' '}
                  (
                  {selectedFrequency === periodicity.Daily
                    ? 'Date'
                    : selectedFrequency === periodicity.Monthly
                      ? 'Month'
                      : selectedFrequency === periodicity.Quarterly
                        ? 'Quarter'
                        : selectedFrequency === periodicity.HalfYearlySemester
                          ? 'Semester'
                          : 'Year'}
                  )
                </span>
                <div
                  className="table-heading-year"
                  style={{ fontSize: '0.75rem', lineHeight: '1rem' }}
                >
                  {unitMeasure ? `Unit Measure: ${unitMeasure}` : ''}
                </div>
              </th>
            )
          })}
    </>
  )

  const renderRows = (countries) => {
    let tableData = countries.map((country) => {
      const combinationData = recentYearSelection
        ? columnCombinations.map((combination) => {
            const data = filteredIndicatorData?.find(
              (item) =>
                item[disaggregations.RefArea] === country.code &&
                Object.entries(combination).every(
                  ([key, value]) => item[key] === value
                )
            )
            const year = data?.data?.xAxis?.[0] ?? '--'
            const value = data?.data?.yAxis?.[0] ?? '--'
            return {
              value,
              data,
              year,
              unitMeasure: data?.[disaggregations.UnitMeasure],
            }
          })
        : columnCombinations.flatMap((combination) =>
            years.map((year) => {
              const data = filteredIndicatorData?.find(
                (item) =>
                  item[disaggregations.RefArea] === country.code &&
                  Object.entries(combination).every(
                    ([key, value]) => item[key] === value
                  )
              )
              const yearIndex = data?.data?.xAxis?.findIndex(
                (item) => item === year.toString()
              )
              const value =
                yearIndex >= 0 ? (data?.data?.yAxis?.[yearIndex] ?? '--') : '--'
              return {
                value,
                data,
                year,
                unitMeasure: data?.[disaggregations.UnitMeasure],
              }
            })
          )
      return { country, combinationData }
    })
    let sortByColumn = '',
      sortColumnOrder = ''
    for (const [key, value] of Object.entries(sortOrder)) {
      if (value) {
        sortByColumn = key
        sortColumnOrder = value
        break
      }
    }
    let sortedTableData = []
    if (sortByColumn === 'economy') {
      sortedTableData =
        sortColumnOrder === 'asc'
          ? tableData.sort((a, b) =>
              !a.country.description
                ? 1
                : !b.country.description
                  ? -1
                  : a.country.description
                      .toLowerCase()
                      .localeCompare(b.country.description.toLowerCase())
            )
          : tableData.sort((a, b) =>
              !a.country.description
                ? 1
                : !b.country.description
                  ? -1
                  : b.country.description
                      .toLowerCase()
                      .localeCompare(a.country.description.toLowerCase())
            )
    } else {
      const combinationDataIndex = sortByColumn.charAt(sortByColumn.length - 1)

      sortedTableData =
        sortColumnOrder === 'asc'
          ? tableData.sort((a, b) => {
              const valA = a.combinationData[combinationDataIndex]?.value
              const valB = b.combinationData[combinationDataIndex]?.value
              const isNumericA = isNumeric(valA)
              const isNumericB = isNumeric(valB)

              if (!isNumericA && !isNumericB) {
                return String(valA).localeCompare(String(valB))
              }
              if (!isNumericA) return 1
              if (!isNumericB) return -1
              return parseFloat(valA) - parseFloat(valB)
            })
          : tableData.sort((a, b) => {
              const valA = a.combinationData[combinationDataIndex]?.value
              const valB = b.combinationData[combinationDataIndex]?.value
              const isNumericA = isNumeric(valA)
              const isNumericB = isNumeric(valB)

              if (!isNumericA && !isNumericB) {
                return String(valB).localeCompare(String(valA))
              }
              if (!isNumericA) return 1
              if (!isNumericB) return -1
              return parseFloat(valB) - parseFloat(valA)
            })
    }

    const economies = sortedTableData.map((item, index) => (
      <tr
        key={item.country.code}
        className={`table-row ${
          selectedCountries.includes(item.country.code)
            ? 'table-row-selected'
            : ''
        }`}
      >
        <td className="table-data">{item.country.description}</td>
      </tr>
    ))
    const data = sortedTableData.map((item, index) => (
      <tr
        key={item.country.code}
        className={`table-row ${
          selectedCountries.includes(item.country.code)
            ? 'table-row-selected'
            : ''
        }`}
      >
        {recentYearSelection
          ? item.combinationData.map((combination, index) => {
              return (
                <td key={index} className="table-data text-align-right">
                  {combination?.value &&
                  combination?.value !== 'null' &&
                  combination?.value !== 'NaN'
                    ? formatNumberOnUnitMeasure({
                      value: combination?.value,
                      unitMeasure:
                        selectedUnitMeasure ||
                        combination?.data?.[disaggregations.UnitMeasure],
                      decimals:
                        filteredIndicatorData?.[0]?.[disaggregations.Decimals],
                      unitType: combination?.data?.[disaggregations.UnitType],
                      isCommaSeparatedFormat: true,
                    })
                    : '--'}{' '}
                  {combination?.year !== '--' && (
                    <span>({combination?.year})</span>
                  )}
                </td>
              )
            })
          : item.combinationData.map((combination, index) => {
              return (
                <td key={index} className="table-data text-align-right">
                  {combination?.value &&
                  combination?.value !== 'null' &&
                  combination?.value !== 'NaN'
                    ? formatNumberOnUnitMeasure({
                        value: combination?.value,
                        unitMeasure:
                          selectedUnitMeasure ||
                          combination?.data?.[disaggregations.UnitMeasure],
                        decimals:
                          filteredIndicatorData?.[0]?.[
                            disaggregations.Decimals
                          ],
                        unitType: combination?.data?.[disaggregations.UnitType],
                        isCommaSeparatedFormat: true,
                      })
                    : '--'}
                </td>
              )
            })}
      </tr>
    ))
    return { economies, data }
  }
  const renderBenchmarkRow = () => {
    let benchmarkList = getBenchmarkList({
      selectedAverages: finalSelectedAverages,
      selectedCountries: selectedCountries.filter((item) => item !== 'all'),
      countryHierarchy,
      chartType: chartTypes.DataTable,
      disaggregationCountries,
    })
    // Filter benchmarkList to include only items that have data for the indicator
    benchmarkList = benchmarkList.filter((item) =>
      countriesData?.map((item) => item?.code).includes(item)
    )
    return benchmarkList?.map((average) => (
      <tr
        key={`datatable-${average}`}
        className="table-row table-row-defaultselected"
      >
        {recentYearSelection
          ? columnCombinations.map((combination, index) => {
              const data = filteredAggregateData.find(
                (item) =>
                  item[disaggregations.RefArea] === average &&
                  Object.entries(combination).every(
                    ([key, value]) => item[key] === value
                  )
              )
              const year = data?.data?.xAxis?.[0] ?? '--'
              const value = data?.data?.yAxis?.[0] ?? '--'

              return (
                <td key={index} className="table-data text-align-right">
                  {value && value !== 'null' && value !== 'NaN'
                    ? formatNumberOnUnitMeasure({
                        value,
                        unitMeasure:
                          selectedUnitMeasure ||
                          data?.[disaggregations.UnitMeasure],
                        decimals:
                          filteredIndicatorData?.[0]?.[
                            disaggregations.Decimals
                          ],
                        unitType: data?.[disaggregations.UnitType],
                        isCommaSeparatedFormat: true,
                      })
                    : '--'}{' '}
                  {year !== '--' && <span>({year})</span>}
                </td>
              )
            })
          : columnCombinations.map((combination, index) =>
              years.map((year) => {
                const data = filteredAggregateData.find(
                  (item) =>
                    item[disaggregations.RefArea] === average &&
                    Object.entries(combination).every(
                      ([key, value]) => item[key] === value
                    )
                )
                const yearIndex = data?.data?.xAxis?.findIndex(
                  (item) => item === year.toString()
                )
                const value =
                  yearIndex >= 0
                    ? (data?.data?.yAxis?.[yearIndex] ?? '--')
                    : '--'

                return (
                  <td key={index} className="table-data text-align-right">
                    {value && value !== 'null' && value !== 'NaN'
                      ? formatNumberOnUnitMeasure({
                          value,
                          unitMeasure:
                            selectedUnitMeasure ||
                            data?.[disaggregations.UnitMeasure],
                          decimals:
                            filteredIndicatorData?.[0]?.[
                              disaggregations.Decimals
                            ],
                          unitType: data?.[disaggregations.UnitType],
                          isCommaSeparatedFormat: true,
                        })
                      : '--'}
                  </td>
                )
              })
            )}
      </tr>
    ))
  }

  const renderBenchmarkRowEconomyColumn = () => {
    let benchmarkList = getBenchmarkList({
      selectedAverages: finalSelectedAverages,
      selectedCountries: selectedCountries.filter((item) => item !== 'all'),
      countryHierarchy,
      chartType: chartTypes.DataTable,
      disaggregationCountries,
    })
    // Filter benchmarkList to include only items that have data for the indicator
    benchmarkList = benchmarkList.filter((item) =>
      countriesData?.map((item) => item?.code).includes(item)
    )
    return benchmarkList?.map((average) => (
      <tr
        key={`datatable-${average}`}
        className="table-row table-row-defaultselected"
      >
        <td className="table-data">
          {getNameFromDisaggregation(
            disaggregationData?.dimensions,
            disaggregations.RefArea,
            average
          )}
        </td>
      </tr>
    ))
  }

  const renderTableData = () => {
    return <>{renderRows(allCountries)?.data}</>
  }
  const renderTableEconomies = () => {
    return <>{renderRows(allCountries)?.economies}</>
  }

  const badges = getBadgeForDataTable(
    selectedFilters,
    disaggregationData,
    unitMeasureName
  )

  const unitMultiplier =
    filteredIndicatorData.length > 0
      ? formatUnitMult(
          disaggregationData,
          filteredIndicatorData?.[0]?.[disaggregations.UnitMult]
        )
      : ''
  const unitMeasure =
    filteredIndicatorData.length > 0
      ? formatUnitMeasure(
          filteredIndicatorData?.[0]?.[disaggregations.UnitMeasure],
          unitMeasureName,
          getUnitMeasureItems(disaggregationData)?.find(
            (item) =>
              item.code ===
              filteredIndicatorData?.[0]?.[disaggregations.UnitMeasure]
          )?.short_label
        )
      : ''
  return (
    <>
      {filteredIndicatorData.length > 0 ? (
        <>
          <div className="tui_comparison_indicator_container">
            <div className="tui_heading_h2_sm">
              {/* <h2>
                {recentYearSelection
                  ? `Most recent values (${currentYear})`
                  : currentYear}
              </h2> */}
              <h2>{indicatorName}</h2>
            </div>
            <p className="tui__text_base">
              {getSingleYearChartSubtitle(
                selectedCountries,
                countriesData,
                recentYearSelection
                  ? `Most recent values ${getRecentYearRange(
                      filteredIndicatorData
                    )}`
                  : `${minYear}-${maxYear}`
              )}
              {unitMultiplier || unitMeasure
                ? `, ${unitMultiplier} ${unitMeasure}`
                : ''}
            </p>
            <div className="tui__badge_container">
              {getBadgesElement(badges)}
            </div>
          </div>

          <div id="data-table-section" className="table-1">
            <div className="table-scroll-wrapper">
              <div className="fixed-column">
                <table border="1" className="data-table-header">
                  <thead>
                    <tr>{renderEconomyHeaders()}</tr>
                    {finalSelectedAverages &&
                      finalSelectedAverages.length > 0 &&
                      renderBenchmarkRowEconomyColumn()}
                    {
                      renderRows(
                        selectedCountries.map((countryCode) =>
                              countriesData.find(
                                (country) => country.code === countryCode
                              )
                            )
                      )?.economies
                    }
                  </thead>
                </table>
              </div>
              <div className="scrollable-columns">
                <table border="1" className="data-table-header">
                  <thead>
                    <tr>{renderDisaggHeaders()}</tr>
                    {finalSelectedAverages &&
                      finalSelectedAverages.length > 0 &&
                      renderBenchmarkRow()}
                    {
                      renderRows(
                        selectedCountries.map((countryCode) =>
                              countriesData.find(
                                (country) => country.code === countryCode
                              )
                            )
                      )?.data
                    }
                  </thead>
                </table>
              </div>
            </div>
          </div>
          {allCountries.length ? (
            <div id="data-table-section" className="table-2">
              <div className="table-scroll-wrapper">
                <div className="fixed-column">
                  <table border="1">
                    <tbody>{renderTableEconomies()}</tbody>
                  </table>
                </div>
                <div className="scrollable-columns">
                  <table border="1">
                    <tbody>{renderTableData()}</tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <NoDataImage imageURL={noDataImageURL} height="345px" width="100%" />
      )}
    </>
  )
}

export default DataTable
